/**
 * DataTablePagination Component
 *
 * Renders pagination controls with page navigation and page size selector.
 */

import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PaginationState } from './types';

interface DataTablePaginationProps {
  pagination: PaginationState;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  pageSizeOptions?: number[];
  loading?: boolean;
  showPageSizeSelector?: boolean;
  showResultsCount?: boolean;
}

export function DataTablePagination({
  pagination,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 20, 50, 100],
  loading = false,
  showPageSizeSelector = true,
  showResultsCount = true
}: DataTablePaginationProps) {
  const { page, limit, total } = pagination;
  const totalPages = Math.ceil(total / limit) || 1;
  const startIndex = (page - 1) * limit + 1;
  const endIndex = Math.min(page * limit, total);

  const canGoPrev = page > 1;
  const canGoNext = page < totalPages;

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | 'ellipsis')[] = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (page > 3) {
        pages.push('ellipsis');
      }

      // Show pages around current
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('ellipsis');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (total === 0) {
    return null;
  }

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
      {/* Mobile view */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={!canGoPrev || loading}
          className={`
            relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
            ${canGoPrev && !loading
              ? 'text-gray-700 bg-white hover:bg-gray-50'
              : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }
          `}
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext || loading}
          className={`
            ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md
            ${canGoNext && !loading
              ? 'text-gray-700 bg-white hover:bg-gray-50'
              : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }
          `}
        >
          Next
        </button>
      </div>

      {/* Desktop view */}
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          {/* Results count */}
          {showResultsCount && (
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex}</span> to{' '}
              <span className="font-medium">{endIndex}</span> of{' '}
              <span className="font-medium">{total}</span> results
            </p>
          )}

          {/* Page size selector */}
          {showPageSizeSelector && onLimitChange && (
            <div className="flex items-center gap-2">
              <label htmlFor="page-size" className="text-sm text-gray-600">
                Show:
              </label>
              <select
                id="page-size"
                value={limit}
                onChange={(e) => onLimitChange(Number(e.target.value))}
                disabled={loading}
                className="rounded-md border-gray-300 py-1 pl-2 pr-8 text-sm focus:border-purple-500 focus:ring-purple-500"
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Page navigation */}
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          {/* Previous button */}
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={!canGoPrev || loading}
            className={`
              relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium
              ${canGoPrev && !loading
                ? 'text-gray-500 bg-white hover:bg-gray-50'
                : 'text-gray-300 bg-gray-100 cursor-not-allowed'
              }
            `}
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          {/* Page numbers */}
          {getPageNumbers().map((pageNum, idx) =>
            pageNum === 'ellipsis' ? (
              <span
                key={`ellipsis-${idx}`}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
              >
                ...
              </span>
            ) : (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                className={`
                  relative inline-flex items-center px-4 py-2 border text-sm font-medium
                  ${page === pageNum
                    ? 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                  }
                  ${loading ? 'cursor-not-allowed' : ''}
                `}
                aria-current={page === pageNum ? 'page' : undefined}
              >
                {pageNum}
              </button>
            )
          )}

          {/* Next button */}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={!canGoNext || loading}
            className={`
              relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium
              ${canGoNext && !loading
                ? 'text-gray-500 bg-white hover:bg-gray-50'
                : 'text-gray-300 bg-gray-100 cursor-not-allowed'
              }
            `}
            aria-label="Next page"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </nav>
      </div>
    </div>
  );
}

export default DataTablePagination;
